import React, { useState, useEffect } from 'react';
import { adminSellRequestService } from '../../services/adminService';

// ─── helpers ──────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  pending: '#ffc107', submitted: '#17a2b8',
  inspection_pending: '#fd7e14', inspection_completed: '#20c997',
  offer_generated: '#6f42c1', offer_accepted: '#28a745',
  offer_rejected: '#dc3545', documents_pending: '#ffc107',
  documents_submitted: '#17a2b8', payment_pending: '#fd7e14',
  payment_completed: '#28a745', pickup_scheduled: '#6610f2',
  car_collected: '#20c997', rc_transfer_in_progress: '#6f42c1',
  completed: '#28a745', price_accepted: '#0d6efd', approved: '#28a745',
};
const APPROVAL_COLORS = { approved: '#28a745', rejected: '#dc3545', pending: '#ffc107' };

function Badge({ value, colorMap }) {
  const color = colorMap[value] || '#6c757d';
  return (
    <span style={{
      background: `${color}20`, color, padding: '5px 11px',
      borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
    }}>
      {value?.replace(/_/g, ' ') || 'N/A'}
    </span>
  );
}

// ─── image/doc viewer card ────────────────────────────────────────────────────
function MediaCard({ url, label }) {
  const isPdf = url?.toLowerCase().includes('.pdf') ||
                url?.toLowerCase().includes('application/pdf');
  return (
    <div style={{
      border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden',
      background: '#fafafa', display: 'flex', flexDirection: 'column',
    }}>
      {isPdf ? (
        <div style={{
          height: 140, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#f0f4ff', flexDirection: 'column', gap: 8,
        }}>
          <span style={{ fontSize: 36 }}>📄</span>
          <span style={{ fontSize: 12, color: '#555' }}>PDF Document</span>
        </div>
      ) : (
        <img
          src={url}
          alt={label}
          style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
          onError={e => { e.target.src = 'https://via.placeholder.com/300x140?text=Not+Available'; }}
        />
      )}
      <div style={{
        padding: '8px 10px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderTop: '1px solid #eee',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>
          {label}
        </span>
        <a href={url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 11, color: '#003566', fontWeight: 600 }}>
          Open ↗
        </a>
      </div>
    </div>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
const SellRequestModal = ({ request, onClose, onApprove, onReject, onRefresh }) => {
  const [activeTab, setActiveTab]     = useState('details');
  const [inspectorId, setInspectorId] = useState('');

  // ── Car photos: API returns images[] as array of URLs ────────────────────
  const carPhotos = Array.isArray(request.images) ? request.images.filter(Boolean) : [];

  // ── Documents: single-attached fields each return a URL string ───────────
  const docFields = [
    { key: 'rc_front',              label: 'RC Front' },
    { key: 'rc_back',               label: 'RC Back' },
    { key: 'insurance_certificate', label: 'Insurance' },
    { key: 'puc_certificate',       label: 'PUC' },
    { key: 'aadhar_front',          label: 'Aadhar Front' },
    { key: 'aadhar_back',           label: 'Aadhar Back' },
    { key: 'pan_card',              label: 'PAN Card' },
    { key: 'service_history_doc',   label: 'Service History' },
  ];
  const uploadedDocs = docFields.filter(d => request[d.key]);

  const handleAssignInspector = async () => {
    if (!inspectorId) { alert('Enter inspector ID'); return; }
    try {
      await adminSellRequestService.assignInspector(request.id, inspectorId);
      alert('Inspector assigned!');
      onRefresh(); onClose();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!window.confirm(`Change status to "${status.replace(/_/g, ' ')}"?`)) return;
    try {
      await adminSellRequestService.updateStatus(request.id, { status });
      alert('Status updated!');
      onRefresh(); onClose();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const tabStyle = (tab) => ({
    padding: '10px 20px', border: 'none', cursor: 'pointer',
    background: activeTab === tab ? '#003566' : 'transparent',
    color: activeTab === tab ? '#FFD60A' : '#555',
    fontWeight: 700, fontSize: 13, borderRadius: '8px 8px 0 0',
    transition: 'all .2s',
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%',
          maxWidth: 820, maxHeight: '90vh', display: 'flex',
          flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.4)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#003566,#001d3d)',
          padding: '18px 24px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <div>
            <h2 style={{ color: '#FFD60A', margin: 0, fontSize: 18 }}>
              Sell Request #{request.id}
            </h2>
            <p style={{ color: 'rgba(255,255,255,.6)', margin: '4px 0 0', fontSize: 13 }}>
              {request.car_brand} {request.model} · {request.year}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)',
            color: '#fff', borderRadius: '50%', width: 34, height: 34,
            cursor: 'pointer', fontSize: 16, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, padding: '12px 24px 0',
          borderBottom: '2px solid #f0f0f0', flexShrink: 0, background: '#fafafa',
        }}>
          {[
            { id: 'details',   label: 'Details' },
            { id: 'photos',    label: `Car Photos (${carPhotos.length})` },
            { id: 'documents', label: `Documents (${uploadedDocs.length})` },
            { id: 'actions',   label: 'Actions' },
          ].map(t => (
            <button key={t.id} style={tabStyle(t.id)} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* ── Details tab ── */}
          {activeTab === 'details' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                ['Car Brand',        request.car_brand],
                ['Model',            request.model],
                ['Year',             request.year],
                ['KM Driven',        `${request.km_driven?.toLocaleString()} km`],
                ['KM Range',         request.km_range],
                ['Fuel Type',        request.fuel_type],
                ['Transmission',     request.transmission],
                ['Ownership',        request.ownership],
                ['Reg. Number',      request.registration_number],
                ['Expected Price',   `₹${parseFloat(request.price_expectation || 0).toLocaleString()}`],
                ['Price Negotiable', request.price_negotiable],
                ['Loan on Car',      request.loan_on_car],
                ['Accident History', request.accident_history],
                ['Extra Details',    request.extra_details],
                ['Status',           request.status?.replace(/_/g, ' ')],
                ['Approval Status',  request.approval_status || 'pending'],
                ['User ID',          `#${request.user_id}`],
                ['Created At',       new Date(request.created_at).toLocaleString()],
              ].map(([label, value]) => (
                <div key={label} style={{
                  background: '#f8faff', border: '1px solid #e8f0ff',
                  borderRadius: 9, padding: '11px 14px',
                }}>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 14, color: '#0A0A0A', fontWeight: 600 }}>
                    {value || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Car Photos tab ── */}
          {activeTab === 'photos' && (
            <>
              {carPhotos.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '60px 20px',
                  color: '#aaa', border: '2px dashed #e0e0e0',
                  borderRadius: 12,
                }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📷</div>
                  <p style={{ margin: 0, fontSize: 15 }}>No car photos uploaded yet</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 12,
                }}>
                  {carPhotos.map((url, i) => (
                    <MediaCard key={i} url={url} label={`Photo ${i + 1}`} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Documents tab ── */}
          {activeTab === 'documents' && (
            <>
              {uploadedDocs.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '60px 20px',
                  color: '#aaa', border: '2px dashed #e0e0e0',
                  borderRadius: 12,
                }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📁</div>
                  <p style={{ margin: 0, fontSize: 15 }}>No documents uploaded yet</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 12,
                }}>
                  {uploadedDocs.map(d => (
                    <MediaCard key={d.key} url={request[d.key]} label={d.label} />
                  ))}
                </div>
              )}

              {docFields.filter(d => !request[d.key]).length > 0 && (
                <div style={{
                  marginTop: 20, background: '#fffbeb',
                  border: '1px solid #fde68a', borderRadius: 10,
                  padding: '12px 16px',
                }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 13, color: '#92400e' }}>
                    ⚠️ Missing Documents:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {docFields.filter(d => !request[d.key]).map(d => (
                      <span key={d.key} style={{
                        background: '#fef3c7', border: '1px solid #fde68a',
                        borderRadius: 6, padding: '3px 10px',
                        fontSize: 12, color: '#92400e', fontWeight: 600,
                      }}>
                        {d.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Actions tab ── */}
          {activeTab === 'actions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

              {/* Approve / Reject */}
              <div style={{ background: '#f8faff', border: '1px solid #d4e6f8', borderRadius: 12, padding: 18 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#003566' }}>Approval</h3>
                {(!request.approval_status || request.approval_status === 'pending') ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => { onApprove(request.id); onClose(); }}
                      style={{
                        flex: 1, padding: '11px 0', background: '#28a745',
                        color: '#fff', border: 'none', borderRadius: 9,
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      }}>
                      ✓ Approve Request
                    </button>
                    <button onClick={() => { onReject(request.id); onClose(); }}
                      style={{
                        flex: 1, padding: '11px 0', background: '#dc3545',
                        color: '#fff', border: 'none', borderRadius: 9,
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      }}>
                      ✕ Reject Request
                    </button>
                  </div>
                ) : (
                  <div style={{
                    padding: '11px 14px', background: '#f0f0f0',
                    borderRadius: 9, fontSize: 14,
                  }}>
                    <strong>Approval Status: </strong>
                    <span style={{
                      color: APPROVAL_COLORS[request.approval_status] || '#555',
                      textTransform: 'capitalize', fontWeight: 700,
                    }}>
                      {request.approval_status}
                    </span>
                  </div>
                )}
              </div>

              {/* Assign Inspector */}
              <div style={{ background: '#f8faff', border: '1px solid #d4e6f8', borderRadius: 12, padding: 18 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#003566' }}>Assign Inspector</h3>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="number"
                    placeholder="Enter Inspector ID"
                    value={inspectorId}
                    onChange={e => setInspectorId(e.target.value)}
                    style={{
                      flex: 1, padding: '10px 13px', border: '1.5px solid #e5e7eb',
                      borderRadius: 9, fontSize: 14, outline: 'none',
                    }}
                  />
                  <button onClick={handleAssignInspector} disabled={!inspectorId}
                    style={{
                      padding: '10px 20px', background: '#003566', color: '#FFD60A',
                      border: 'none', borderRadius: 9, fontWeight: 700,
                      fontSize: 14, cursor: 'pointer', opacity: inspectorId ? 1 : .5,
                    }}>
                    Assign
                  </button>
                </div>
              </div>

              {/* Update Status */}
              <div style={{ background: '#f8faff', border: '1px solid #d4e6f8', borderRadius: 12, padding: 18 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#003566' }}>Update Status</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    'inspection_pending', 'inspection_completed',
                    'offer_generated', 'documents_pending',
                    'payment_pending', 'completed',
                  ].map(s => (
                    <button key={s} onClick={() => handleStatusUpdate(s)}
                      style={{
                        padding: '10px 14px', background: '#fff',
                        border: '1.5px solid #d4e6f8', borderRadius: 9,
                        color: '#003566', fontWeight: 600, fontSize: 13,
                        cursor: 'pointer', textTransform: 'capitalize',
                        transition: 'all .2s',
                      }}
                      onMouseOver={e => e.target.style.background = '#eef4ff'}
                      onMouseOut={e => e.target.style.background = '#fff'}
                    >
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const SellRequestsManagement = () => {
  const [requests,        setRequests]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal,       setShowModal]       = useState(false);
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [searchTerm,      setSearchTerm]      = useState('');

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await adminSellRequestService.getAllSellRequests();
      // API returns { success, data: [...] }
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setRequests(list);
    } catch (e) {
      console.error('Error loading sell requests:', e);
      alert('Failed to load sell requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this request?')) return;
    try {
      await adminSellRequestService.approveSellRequest(id);
      alert('Approved!');
      loadRequests();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    try {
      await adminSellRequestService.rejectSellRequest(id);
      alert('Rejected!');
      loadRequests();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const filteredRequests = requests.filter(r => {
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    const q = searchTerm.toLowerCase();
    const matchSearch =
      r.car_brand?.toLowerCase().includes(q) ||
      r.model?.toLowerCase().includes(q) ||
      r.registration_number?.toLowerCase().includes(q) ||
      String(r.user_id).includes(q);
    return matchStatus && matchSearch;
  });

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <p>Loading sell requests…</p>
    </div>
  );

  return (
    <div className="sell-requests-management">
      {/* Header */}
      <div className="management-header">
        <div>
          <h1>Sell Requests Management</h1>
          <p>Review and manage car selling requests ({requests.length} total)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="management-filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search brand, model, reg. no., user ID…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          {['all','pending','inspection_pending','approved','rejected'].map(s => (
            <button key={s}
              className={`filter-tab ${filterStatus === s ? 'active' : ''}`}
              onClick={() => setFilterStatus(s)}
            >
              {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="management-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Car</th>
              <th>User</th>
              <th>Price</th>
              <th>Photos</th>
              <th>Docs</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map(r => {
              // images[] is now an array of URLs from the API
              const photoCount = Array.isArray(r.images) ? r.images.filter(Boolean).length : 0;

              const docCount = [
                r.rc_front, r.rc_back, r.insurance_certificate,
                r.puc_certificate, r.aadhar_front, r.aadhar_back,
                r.pan_card, r.service_history_doc,
              ].filter(Boolean).length;

              return (
                <tr key={r.id}>
                  <td>#{r.id}</td>
                  <td>
                    <div className="request-details">
                      <h4>{r.car_brand} {r.model}</h4>
                      <p>{r.year} · {r.km_driven?.toLocaleString()} km</p>
                      {r.registration_number && (
                        <p style={{ fontSize: 11, color: '#888' }}>{r.registration_number}</p>
                      )}
                    </div>
                  </td>
                  <td><strong>User #{r.user_id}</strong></td>
                  <td><strong>₹{parseFloat(r.price_expectation || 0).toLocaleString()}</strong></td>
                  <td>
                    <button className="view-images-btn"
                      onClick={() => { setSelectedRequest(r); setShowModal(true); }}>
                      📷 {photoCount}
                    </button>
                  </td>
                  <td>
                    <button className="view-images-btn"
                      onClick={() => { setSelectedRequest(r); setShowModal(true); }}>
                      📁 {docCount}
                    </button>
                  </td>
                  <td><Badge value={r.status} colorMap={STATUS_COLORS} /></td>
                  <td><Badge value={r.approval_status || 'pending'} colorMap={APPROVAL_COLORS} /></td>
                  <td>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-view"
                        onClick={() => { setSelectedRequest(r); setShowModal(true); }}
                        title="View Details">👁️</button>
                      {(!r.approval_status || r.approval_status === 'pending') && (
                        <>
                          <button className="btn-approve"
                            onClick={() => handleApprove(r.id)} title="Approve">✓</button>
                          <button className="btn-reject"
                            onClick={() => handleReject(r.id)} title="Reject">✕</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredRequests.length === 0 && (
          <div className="no-results"><p>No sell requests found</p></div>
        )}
      </div>

      {showModal && selectedRequest && (
        <SellRequestModal
          request={selectedRequest}
          onClose={() => { setShowModal(false); setSelectedRequest(null); }}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefresh={loadRequests}
        />
      )}
    </div>
  );
};

export default SellRequestsManagement;